import {
  Attribute,
  AttributeGroup,
  BooleanAttribute,
  NumberAttribute,
  SimpleAttribute,
  StringAttribute,
  TimestampAttribute,
} from "./type";
import { Buckets, Bucket, standardBuckets, autoBuckets } from "../bucket";
import { isArray } from "@cartographerio/guard";

export function simpleAttr(
  props: Omit<SimpleAttribute, "type">
): SimpleAttribute {
  return {
    type: "SimpleAttribute",
    ...props,
  };
}

type BucketsProp<A> = "auto" | Bucket<A>[] | Buckets<A>;

function wrapBuckets<A>(buckets: BucketsProp<A>): Buckets<A> {
  return buckets === "auto"
    ? autoBuckets<A>({})
    : isArray(buckets)
    ? standardBuckets<A>(buckets)
    : buckets;
}

export interface StringAttrProps
  extends Omit<
    StringAttribute,
    | "type"
    | "propertyType"
    | "propertyName"
    | "buckets"
    | "showDistributionChart"
  > {
  propertyName?: string;
  buckets: BucketsProp<string | null>;
  showDistributionChart?: boolean;
}

export function stringAttr(props: StringAttrProps): StringAttribute {
  const {
    attributeId,
    propertyName = attributeId,
    buckets,
    showDistributionChart = true,
    ...rest
  } = props;
  return {
    type: "PropertyAttribute",
    attributeId,
    propertyType: "string",
    propertyName,
    buckets: wrapBuckets(buckets),
    showDistributionChart,
    ...rest,
  };
}

export interface NumberAttrProps
  extends Omit<
    NumberAttribute,
    | "type"
    | "propertyType"
    | "propertyName"
    | "buckets"
    | "showDistributionChart"
    | "showTimelineChart"
  > {
  propertyName?: string;
  buckets: BucketsProp<number | null>;
  showDistributionChart?: boolean;
  showTimelineChart?: boolean;
}

export function numberAttr(props: NumberAttrProps): NumberAttribute {
  const {
    attributeId,
    propertyName = attributeId,
    buckets,
    showDistributionChart = true,
    showTimelineChart = true,
    ...rest
  } = props;
  return {
    type: "PropertyAttribute",
    attributeId,
    propertyType: "number",
    propertyName,
    buckets: wrapBuckets(buckets),
    showDistributionChart,
    showTimelineChart,
    ...rest,
  };
}

interface BooleanAttrProps
  extends Omit<
    BooleanAttribute,
    | "type"
    | "propertyType"
    | "propertyName"
    | "buckets"
    | "showDistributionChart"
  > {
  propertyName?: string;
  buckets: BucketsProp<boolean | null>;
  showDistributionChart?: boolean;
}

export function booleanAttr(props: BooleanAttrProps): BooleanAttribute {
  const {
    attributeId,
    propertyName = attributeId,
    buckets,
    showDistributionChart = true,
    ...rest
  } = props;
  return {
    type: "PropertyAttribute",
    attributeId,
    propertyType: "boolean",
    propertyName,
    buckets: wrapBuckets(buckets),
    showDistributionChart,
    ...rest,
  };
}

interface TimestampAttrProps
  extends Omit<
    TimestampAttribute,
    | "type"
    | "propertyType"
    | "propertyName"
    | "buckets"
    | "showDistributionChart"
  > {
  propertyName?: string;
  buckets: BucketsProp<number | null>;
  showDistributionChart?: boolean;
}

export function timestampAttr(props: TimestampAttrProps): TimestampAttribute {
  const {
    attributeId,
    propertyName = attributeId,
    buckets,
    showDistributionChart = true,
    ...rest
  } = props;
  return {
    type: "PropertyAttribute",
    attributeId,
    propertyType: "timestamp",
    propertyName,
    buckets: wrapBuckets(buckets),
    showDistributionChart,
    ...rest,
  };
}

export function attributeGroup(
  props: Attribute[] | Omit<AttributeGroup, "type">
): AttributeGroup {
  if (isArray(props)) {
    return { type: "AttributeGroup", attributes: props };
  } else {
    return { type: "AttributeGroup", ...props };
  }
}
